'use client';

import React, { useMemo } from 'react';
import { Box, Typography } from 'src/components';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import useUserSettings from 'src/hooks/users/useUserSettings';
import { EncountersView, VolumeType } from 'src/context/reducers';
import { generateSummaryString } from './utils';

const previewContent = {
  [EncountersView.NEW_CODE_SET]: {
    title: 'Preview & Confirm',
    description:
      'Preview your new volume criteria and confirm by giving the new volume set a name.',
  },
};

// --- Component ---

const SearchPreview = () => {
  const { state } = useEncounters();
  const { getSetting } = useUserSettings();
  const payerMode = getSetting<'treating' | 'sending' | 'receiving'>('prospectMode') || 'treating';
  const { volumeType: selectedType, billingCodes } = state;
  const content =
    previewContent[state.view as EncountersView] ||
    previewContent[EncountersView.NEW_CODE_SET];

  const hasSelectedCodes = useMemo(
    () => billingCodes.groups.some((group) => group.codes.length > 0),
    [billingCodes.groups]
  );

  const summaryText = useMemo(
    () =>
      hasSelectedCodes ? generateSummaryString(billingCodes, payerMode) : 'Not Available',
    [billingCodes, hasSelectedCodes, payerMode]
  );

  const isEncountersView = selectedType === VolumeType.ENCOUNTERS;
  const subTitle = isEncountersView ? 'Encounters' : 'Total Claims';

  return (
    <Box className="flex flex-col gap-6 p-6">
      <Box className="flex flex-col gap-1">
        <Typography className="text-primary text-lg font-bold">
          {content.title}
        </Typography>
        <Typography className="text-tertiary text-sm">
          {content.description}
        </Typography>
      </Box>
      <Box
        className={`rounded-lg bg-level-1 p-4 justify-start flex flex-col ${
          !hasSelectedCodes ? 'items-center' : 'items-start'
        }`}
      >
        {hasSelectedCodes ? (
          <>
            <Typography className="text-primary text-sm">
              Show me {subTitle} where patients were:
            </Typography>
            <Typography className="text-primary text-sm mt-2 leading-7">
              {summaryText}.
            </Typography>
          </>
        ) : (
          <Typography className="text-primary text-sm">
            {summaryText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SearchPreview;
